// DJ Default Settings Service
// Stores default event settings for each DJ in Firestore

import { doc, getDoc, setDoc, deleteField, updateDoc } from 'firebase/firestore';
import { db } from './firebase';
import type { PlayMode } from './playModes';
import { clampQueueDepth } from '../utils/playerQueueSync';

export interface DJDefaultEventSettings {
  playMode?: PlayMode;
  maxQueueSize: number;
  queueMode: 'standard' | 'ranked' | 'fifo';
  isActive: boolean;
  allowTips: boolean;
  autoApprove: boolean;
  filterExplicit: boolean;
  blockExplicit: boolean;
  warnExplicit: boolean;
  cooldownMinutes: number;
  maxRequestsPerUser: number;
  downvotingEnabled: boolean;
  voteOutEnabled: boolean;
  voteOutThresholdPercent: number;
  upvoteOverrideEnabled: boolean;
  upvoteOverridePercent: number;
  rankingMinSongs: number;
  rankingMaxSongs: number;
  rankingMinParticipants: number;
  preEventQueueView: 'full' | 'own_only' | 'blind';
  postCloseQueueView: 'show' | 'hide';
  duringEventQueueView: 'now_and_upcoming' | 'now_only' | 'hide';
  crossfadeSeconds: number;
  rankSize: number;
  rankingStalenessMinutes: number;
  autoRejectBlocked: boolean;
  doomVotingEnabled: boolean;
  doomVotingMaxVotes: number;
  doomVotingActiveMinutes: number;
  doomVotingRestMinutes: number;
  superRequestEnabled: boolean;
  superRequestThresholdType: 'count' | 'percentage';
  superRequestThresholdCount: number;
  superRequestThresholdPercent: number;
  maxNominationsPerUser: number;
  allowAttendeeFlagging: boolean;
  queueDepth: number;
  lockWhenQueued: boolean;
  autoMatchFromLibrary: boolean;
  overflowEnabled: boolean;
  hideRankingInfo: boolean;
  lotteryAllowRepeatWinners: boolean;
  linkConfidenceThreshold: number;
}

// Default values if no custom defaults are saved
export const SYSTEM_DEFAULTS: DJDefaultEventSettings = {
  maxQueueSize: 50,
  queueMode: 'standard',
  isActive: true,
  allowTips: true,
  autoApprove: false,
  filterExplicit: false,
  blockExplicit: false,
  warnExplicit: true,
  cooldownMinutes: 5,
  maxRequestsPerUser: 5,
  downvotingEnabled: false,
  voteOutEnabled: true,
  voteOutThresholdPercent: 50,
  upvoteOverrideEnabled: false,
  upvoteOverridePercent: 70,
  rankingMinSongs: 1,
  rankingMaxSongs: 0,
  rankingMinParticipants: 3,
  preEventQueueView: 'full',
  postCloseQueueView: 'show',
  duringEventQueueView: 'now_and_upcoming',
  crossfadeSeconds: 6,
  rankSize: 5,
  rankingStalenessMinutes: 0,
  autoRejectBlocked: false,
  doomVotingEnabled: false,
  doomVotingMaxVotes: 10,
  doomVotingActiveMinutes: 3,
  doomVotingRestMinutes: 5,
  superRequestEnabled: false,
  superRequestThresholdType: 'count',
  superRequestThresholdCount: 5,
  superRequestThresholdPercent: 10,
  maxNominationsPerUser: 0,
  allowAttendeeFlagging: false,
  queueDepth: 1,
  lockWhenQueued: false,
  autoMatchFromLibrary: false,
  overflowEnabled: false,
  hideRankingInfo: false,
  lotteryAllowRepeatWinners: true,
  linkConfidenceThreshold: 2,
};

// Copied into every new event's settings (ITER-171)
export function playerQueueSettingsFromDefaults(
  defaults: Partial<Pick<DJDefaultEventSettings, 'queueDepth' | 'lockWhenQueued'>>,
): { queueDepth: number; lockWhenQueued: boolean } {
  return {
    queueDepth: clampQueueDepth(defaults.queueDepth ?? SYSTEM_DEFAULTS.queueDepth),
    lockWhenQueued: defaults.lockWhenQueued ?? SYSTEM_DEFAULTS.lockWhenQueued,
  };
}

const confidenceThresholdCache = new Map<string, number>();

// Get default settings for a specific DJ
export async function getDJDefaults(djId: string): Promise<DJDefaultEventSettings> {
  const userRef = doc(db, 'users', djId);
  const snap = await getDoc(userRef);
  if (snap.exists() && snap.data().defaultEventSettings) {
    return { ...SYSTEM_DEFAULTS, ...snap.data().defaultEventSettings };
  }
  return SYSTEM_DEFAULTS;
}

// The DJ's "double-check below N clean uses" warning level; 0 turns the warning off
export async function getLinkConfidenceThreshold(djId: string): Promise<number> {
  const cached = confidenceThresholdCache.get(djId);
  if (cached !== undefined) return cached;
  try {
    const value = (await getDJDefaults(djId)).linkConfidenceThreshold;
    const threshold = Number.isInteger(value) && value >= 0 && value <= 20 ? value : SYSTEM_DEFAULTS.linkConfidenceThreshold;
    confidenceThresholdCache.set(djId, threshold);
    return threshold;
  } catch (err) {
    console.warn('[djDefaults] Could not read the link confidence threshold; using the default:', err);
    return SYSTEM_DEFAULTS.linkConfidenceThreshold;
  }
}

// Save default settings for a specific DJ
export async function saveDJDefaults(djId: string, settings: Partial<DJDefaultEventSettings>): Promise<void> {
  confidenceThresholdCache.delete(djId);
  const current = await getDJDefaults(djId);
  const merged = { ...current, ...settings };
  const userRef = doc(db, 'users', djId);
  await setDoc(userRef, { defaultEventSettings: merged }, { merge: true });
}

// Reset to system defaults for a specific DJ
export async function resetDJDefaults(djId: string): Promise<void> {
  confidenceThresholdCache.delete(djId);
  const userRef = doc(db, 'users', djId);
  await updateDoc(userRef, { defaultEventSettings: deleteField() });
}

// Check if DJ has custom defaults
export async function hasCustomDefaults(djId: string): Promise<boolean> {
  const userRef = doc(db, 'users', djId);
  const snap = await getDoc(userRef);
  return snap.exists() && !!snap.data().defaultEventSettings;
}
